import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    AlertTriangle, CheckCircle2, CloudUpload, Download, FileText, GitBranch, Loader2,
    RefreshCw, Sparkles, XCircle,
} from 'lucide-react';
import { ApiError, useApi } from '../../lib/api';
import { ProposalChat } from './ProposalChat';
import type {
    ApiResponse, Proposal, ProposalCapabilities, ProposalDocument,
    ProposalRun, ProposalRunEvent,
} from '../../types';

/**
 * Parties de l'écran des propositions commerciales.
 *
 * Ce fichier était une modale. Paramétrer une génération, suivre son
 * avancement pendant plusieurs minutes et discuter avec l'agent ne tient pas
 * dans une fenêtre surgissante : elle se ferme d'un clic à côté, elle ne se
 * partage pas par un lien, et elle impose sa largeur au moment où l'on a le plus
 * besoin de place. Les parties vivent donc ici, et la page les compose.
 *
 * La génération dure des minutes, donc l'écran ne l'attend pas : il suit
 * l'exécution en interrogeant son journal. Ce suivi n'est pas cosmétique. Sans
 * lui, l'utilisateur n'aurait qu'un rond qui tourne pendant plusieurs minutes et
 * n'aurait aucun moyen de distinguer une génération lente d'une génération
 * bloquée.
 */

/** Libellés des étapes, dans l'ordre où elles arrivent. */
export const EVENT_LABELS: Record<string, string> = {
    queued: 'En file',
    started: 'Démarrage',
    consommation: 'Jetons consommés',
    activite: 'Travail du modèle',
    template_loaded: 'Modèle chargé',
    documents_charges: 'Documents du client lus',
    tools_resolved: 'Outils retenus',
    briefing_demarre: 'Recherche et briefing',
    briefing_pret: 'Briefing établi',
    plan_started: 'Rédaction du plan',
    section_ecrite: 'Section rédigée',
    plan_incomplet: 'Plan incomplet',
    correction_ecartee: 'Correction écartée',
    consignes_prises: 'Consignes du consultant prises en compte',
    consignes_en_vol: 'Consigne reçue pendant la génération',
    iteration_demarree: 'Reprise de la version précédente',
    propositions_de_reference: 'Propositions de référence chargées',
    reprise_apres_interruption: 'Reprise après interruption',
    plan_rejected: 'Plan refusé, correction demandée',
    plan_accepted: 'Plan accepté',
    plan_ready: 'Plan produit',
    rendered: 'Document rendu',
    relecture: 'Relecture des slides',
    correction_demandee: 'Correction demandée',
    correction_appliquee: 'Correction appliquée',
    correction_abandonnee: 'Correction abandonnée',
    version_created: 'Version enregistrée',
    sharepoint_deposited: 'Déposé dans SharePoint',
    sharepoint_failed: 'Dépôt SharePoint en échec',
    sharepoint_skipped: 'Dépôt SharePoint non tenté',
    logo_missing: 'Logo client introuvable',
    cancelled: 'Annulée',
    failed: 'Échec',
};

/**
 * Libellé d'une étape, jamais son nom technique.
 *
 * Une étape sans entrée dans la table s'affichait telle quelle, et c'est ainsi
 * que « tools_resolved » est apparu au milieu d'un écran français. À défaut de
 * traduction, on rend au moins la valeur lisible.
 */
export function libelleEtape(type: string): string {
    return EVENT_LABELS[type]
        || type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

export const STATUS_LABELS: Record<string, string> = {
    draft: 'Brouillon',
    generating: 'Génération en cours',
    ready: 'Prête',
    failed: 'Échec',
    cancelled: 'Annulée',
    scoping: 'Cadrage',
    awaiting_input: 'En attente de réponse',
};

/** Ce qui empêche de générer, dit avant que l'utilisateur remplisse le formulaire. */
export function Readiness({ caps }: { caps: ProposalCapabilities }) {
    if (caps.blockers?.length) return <Blockers reasons={caps.blockers} />;

    return (
        <p className="text-xs text-[var(--color-text-muted)]">
            {/* La trame ne se dit plus ici : elle se choisit dans le formulaire. */}
            {caps.runsToday} génération{caps.runsToday > 1 ? 's' : ''} sur les
            {' '}{caps.settings?.maxRunsPerUserPerDay} autorisées aujourd'hui.
            {!caps.sharepointConfigured
                && " Le dépôt SharePoint est indisponible, les documents restent téléchargeables."}
        </p>
    );
}

/**
 * Motifs de blocage.
 *
 * Un bouton grisé sans explication est le pire des cas : l'utilisateur ne sait
 * ni pourquoi, ni s'il peut y remédier, ni qui appeler. Chaque motif vient du
 * serveur, qui seul connaît la cause réelle.
 */
export function Blockers({ reasons }: { reasons: string[] }) {
    return (
        <div className="rounded-lg border border-[var(--color-warning)] bg-[var(--color-warning-light)] p-3 space-y-1">
            {reasons.map((reason) => (
                <p key={reason} className="text-sm text-[var(--color-text)] flex gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-[var(--color-warning)]" />
                    {reason}
                </p>
            ))}
        </div>
    );
}

/**
 * Options arrêtées à chaque génération.
 *
 * Elles vivaient dans l'écran d'administration, donc une fois pour toutes. Une
 * note de cadrage de trois slides n'appelle pourtant ni la même trame, ni le
 * même moteur, ni les mêmes outils qu'une réponse à appel d'offres. Le
 * composant est partagé entre la création et la régénération, pour que les deux
 * offrent exactement les mêmes choix.
 */
interface RunOptionsState {
    language: string;
    templateId: string;
    model: string;
    effort: string;
    tools: Record<string, boolean>;
    webUnrestricted: boolean;
    webAllowlist: string;
    /** Propositions passées désignées comme références. */
    libraryIds: string[];
    /** Parties de la trame à intégrer. Vide signifie toutes. */
    sections: string[];
}

export function initialRunOptions(caps?: ProposalCapabilities): RunOptionsState {
    return {
        language: 'fr',
        templateId: caps?.template?.id || caps?.templates?.[0]?.id || '',
        model: caps?.settings?.model || '',
        effort: caps?.settings?.effort || 'high',
        tools: { ...(caps?.defaultTools || {}) },
        webUnrestricted: (caps?.webAllowlist?.length ?? 0) === 0,
        webAllowlist: (caps?.webAllowlist || []).join(', '),
        libraryIds: [],
        // Toutes les parties au départ : retirer se décide, garder est le défaut.
        sections: [...(caps?.sections || [])],
    };
}

export function appendRunOptions(body: FormData, options: RunOptionsState): void {
    if (options.language) body.append('language', options.language);
    if (options.templateId) body.append('templateId', options.templateId);
    if (options.model) body.append('model', options.model);
    if (options.effort) body.append('effort', options.effort);
    body.append('tools', JSON.stringify(options.tools));
    body.append('webUnrestricted', options.webUnrestricted ? 'true' : 'false');
    if (!options.webUnrestricted && options.webAllowlist.trim()) {
        body.append('webAllowlist', options.webAllowlist.trim());
    }
    if (options.libraryIds.length > 0) {
        body.append('libraryIds', JSON.stringify(options.libraryIds));
    }
    if (options.sections.length > 0) {
        body.append('sections', JSON.stringify(options.sections));
    }
}

export function RunOptionsFields({ caps, value, onChange }: {
    caps?: ProposalCapabilities;
    value: RunOptionsState;
    onChange: (next: RunOptionsState) => void;
}) {
    const templates = caps?.templates || [];
    const tools = caps?.tools || [];
    const library = caps?.library || [];
    const toujoursActifs = tools.filter((tool) => tool.available && tool.alwaysOn);
    const optionnels = tools.filter((tool) => !tool.alwaysOn);
    const webChosen = tools.some((tool) => tool.web && (tool.alwaysOn || value.tools[tool.key]));
    const referencesDemandees = tools.some(
        (tool) => tool.needsSelection && tool.available && value.tools[tool.key],
    );

    return (
        <div className="space-y-4 border-t border-[var(--color-border)] pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div>
                    <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                        Langue
                    </label>
                    <select
                        className="input"
                        value={value.language}
                        onChange={(event) => onChange({ ...value, language: event.target.value })}
                    >
                        {(caps?.languages || []).map((choice) => (
                            <option key={choice.id} value={choice.id}>{choice.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                        Trame
                    </label>
                    <select
                        className="input"
                        value={value.templateId}
                        onChange={(event) => onChange({ ...value, templateId: event.target.value })}
                    >
                        {templates.length === 0 && <option value="">Modèle actif</option>}
                        {templates.map((template) => (
                            <option key={template.id} value={template.id}>{template.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                        Moteur
                    </label>
                    <select
                        className="input"
                        value={value.model}
                        onChange={(event) => onChange({ ...value, model: event.target.value })}
                    >
                        {(caps?.models || []).map((choice) => (
                            <option key={choice.id} value={choice.id}>{choice.label}</option>
                        ))}
                    </select>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                        {caps?.models?.find((choice) => choice.id === value.model)?.hint}
                    </p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                        Effort
                    </label>
                    <select
                        className="input"
                        value={value.effort}
                        onChange={(event) => onChange({ ...value, effort: event.target.value })}
                    >
                        {(caps?.efforts || []).map((choice) => (
                            <option key={choice.id} value={choice.id}>{choice.label}</option>
                        ))}
                    </select>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                        {caps?.efforts?.find((choice) => choice.id === value.effort)?.hint}
                    </p>
                </div>
            </div>

            {(caps?.sections || []).length > 0 && (
                <div>
                    <p className="block text-sm font-medium text-[var(--color-text)] mb-2">
                        Parties à intégrer
                    </p>
                    {/* Retirer une partie la retire vraiment : le sommaire, la
                        numérotation des séparateurs et le fil d'Ariane ne portent
                        que ce qui reste. Une partie qu'on ne veut pas valait mieux
                        être retirée que remplie par obligation. */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {(caps?.sections || []).map((section) => (
                            <label key={section} className="flex gap-2 items-center text-sm">
                                <input
                                    type="checkbox"
                                    checked={value.sections.includes(section)}
                                    onChange={(event) => onChange({
                                        ...value,
                                        // L'ordre de la trame est conservé, quel que
                                        // soit l'ordre des clics.
                                        sections: (caps?.sections || []).filter((libelle) => (
                                            libelle === section
                                                ? event.target.checked
                                                : value.sections.includes(libelle)
                                        )),
                                    })}
                                />
                                <span className="text-[var(--color-text)]">{section}</span>
                            </label>
                        ))}
                    </div>
                    {value.sections.length === 0 && (
                        <p className="text-xs text-[var(--color-warning)] mt-1">
                            Aucune partie retenue : la proposition en porterait toutes.
                        </p>
                    )}
                </div>
            )}

            <div>
                <p className="block text-sm font-medium text-[var(--color-text)] mb-2">Outils</p>
                {/* Les outils toujours actifs sont dits, pas proposés : chercher
                    sur le web et savoir compter font partie du métier, et une
                    case qu'on ne gagne rien à décocher n'est pas un choix. */}
                {toujoursActifs.length > 0 && (
                    <div className="mb-2 space-y-1">
                        {toujoursActifs.map((tool) => (
                            <p key={tool.key} className="text-sm flex gap-2 items-start">
                                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-[var(--color-success)]" />
                                <span>
                                    <span className="text-[var(--color-text)]">{tool.label}</span>
                                    <span className="text-xs text-[var(--color-text-muted)]"> — toujours actif</span>
                                    <span className="block text-xs text-[var(--color-text-muted)]">
                                        {tool.description}
                                    </span>
                                </span>
                            </p>
                        ))}
                    </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {optionnels.map((tool) => (
                        <label
                            key={tool.key}
                            className={`flex gap-2 items-start text-sm ${tool.available ? '' : 'opacity-60'}`}
                            title={tool.available ? tool.description : tool.unavailableReason}
                        >
                            <input
                                type="checkbox"
                                className="mt-1"
                                disabled={!tool.available}
                                checked={Boolean(value.tools[tool.key])}
                                onChange={(event) => onChange({
                                    ...value,
                                    tools: { ...value.tools, [tool.key]: event.target.checked },
                                })}
                            />
                            <span>
                                <span className="text-[var(--color-text)]">{tool.label}</span>
                                <span className="block text-xs text-[var(--color-text-muted)]">
                                    {tool.available ? tool.description : tool.unavailableReason}
                                </span>
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Cocher « s'inspirer de propositions passées » sans dire
                lesquelles ne ferait rien : le choix est ici, sous la case. */}
            {referencesDemandees && (
                <div>
                    <p className="block text-sm font-medium text-[var(--color-text)] mb-2">
                        Propositions dont s'inspirer
                    </p>
                    {library.length === 0 ? (
                        <p className="text-xs text-[var(--color-text-muted)]">
                            Aucune proposition n'a encore été chargée dans l'écran Admin,
                            onglet Propositions.
                        </p>
                    ) : (
                        <div className="space-y-1">
                            {library.map((entry) => (
                                <label key={entry.id} className="flex gap-2 items-start text-sm">
                                    <input
                                        type="checkbox"
                                        className="mt-1"
                                        checked={value.libraryIds.includes(entry.id)}
                                        onChange={(event) => onChange({
                                            ...value,
                                            libraryIds: event.target.checked
                                                ? [...value.libraryIds, entry.id]
                                                : value.libraryIds.filter((id) => id !== entry.id),
                                        })}
                                    />
                                    <span>
                                        <span className="text-[var(--color-text)]">{entry.title}</span>
                                        {entry.client && (
                                            <span className="text-xs text-[var(--color-text-muted)]">
                                                {' '}— {entry.client}
                                            </span>
                                        )}
                                        {entry.notes && (
                                            <span className="block text-xs text-[var(--color-text-muted)]">
                                                {entry.notes}
                                            </span>
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {webChosen && (
                <div>
                    <label className="flex gap-2 items-center text-sm text-[var(--color-text)]">
                        <input
                            type="checkbox"
                            checked={value.webUnrestricted}
                            onChange={(event) => onChange({
                                ...value,
                                webUnrestricted: event.target.checked,
                            })}
                        />
                        Ne pas restreindre les domaines consultés
                    </label>
                    {!value.webUnrestricted && (
                        <>
                            <input
                                className="input mt-2"
                                value={value.webAllowlist}
                                onChange={(event) => onChange({
                                    ...value,
                                    webAllowlist: event.target.value,
                                })}
                                placeholder="learn.microsoft.com, clippit.fr"
                            />
                            <p className="text-xs text-[var(--color-text-muted)] mt-1">
                                Noms de domaine séparés par des virgules. Laisser vide revient à
                                ne pas restreindre.
                            </p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export function CreateForm({ opportunityId, caps, disabled, onCancel, onCreated }: {
    opportunityId: string;
    caps?: ProposalCapabilities;
    disabled: boolean;
    onCancel: () => void;
    onCreated: (proposal: Proposal) => void;
}) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [form, setForm] = useState({
        prompt: '', title: '', maxAmount: '', dailyRate: '', transcript: '',
    });
    const [logo, setLogo] = useState<File | null>(null);
    const [deck, setDeck] = useState<File | null>(null);
    const [transcriptFile, setTranscriptFile] = useState<File | null>(null);
    const [documents, setDocuments] = useState<File[]>([]);
    const [options, setOptions] = useState<RunOptionsState>(() => initialRunOptions(caps));
    const [error, setError] = useState<string | null>(null);

    // Les capacités arrivent après le premier rendu : sans cela, les listes
    // déroulantes resteraient sur leur valeur vide alors que le serveur a
    // répondu.
    useEffect(() => {
        if (caps) setOptions((current) => (current.model ? current : initialRunOptions(caps)));
    }, [caps]);

    const mutation = useMutation({
        mutationFn: async () => {
            const body = new FormData();
            body.append('opportunityId', opportunityId);
            body.append('prompt', form.prompt);
            if (form.title.trim()) body.append('title', form.title.trim());
            if (form.maxAmount.trim()) body.append('maxAmount', form.maxAmount.trim());
            if (form.dailyRate.trim()) body.append('dailyRate', form.dailyRate.trim());
            if (form.transcript.trim()) body.append('transcript', form.transcript.trim());
            if (logo) body.append('clientLogo', logo);
            if (deck) body.append('referenceDeck', deck);
            if (transcriptFile) body.append('transcriptFile', transcriptFile);
            for (const document of documents) body.append('referenceDocs', document);
            appendRunOptions(body, options);
            return api.upload<ApiResponse<Proposal>>('/api/proposals', body);
        },
        onSuccess: (response) => {
            void queryClient.invalidateQueries({ queryKey: ['proposals', opportunityId] });
            void queryClient.invalidateQueries({ queryKey: ['proposal-capabilities'] });
            onCreated(response.data);
        },
        onError: (mutationError: ApiError) => setError(mutationError.message),
    });

    const tooShort = form.prompt.trim().length < 30;

    return (
        <div className="card space-y-4">
            <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                    Besoin à couvrir <span className="text-[var(--color-error)]">*</span>
                </label>
                <textarea
                    className="input min-h-28"
                    value={form.prompt}
                    onChange={(event) => setForm({ ...form, prompt: event.target.value })}
                    placeholder="Ce que le client demande, ce qui est en jeu, ce qui doit figurer dans la proposition."
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    {tooShort
                        ? 'Trente caractères au minimum : une consigne trop courte ne cadre rien.'
                        : `${form.prompt.trim().length} caractères.`}
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                        Titre de la proposition
                    </label>
                    <input
                        className="input"
                        value={form.title}
                        onChange={(event) => setForm({ ...form, title: event.target.value })}
                        placeholder="Laisser vide pour que le titre soit proposé"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                        Montant maximum (EUR)
                    </label>
                    <input
                        className="input"
                        type="number"
                        min={0}
                        value={form.maxAmount}
                        onChange={(event) => setForm({ ...form, maxAmount: event.target.value })}
                        placeholder="Aucun plafond"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                        TJM moyen (EUR)
                    </label>
                    <input
                        className="input"
                        type="number"
                        min={0}
                        value={form.dailyRate}
                        onChange={(event) => setForm({ ...form, dailyRate: event.target.value })}
                        placeholder="Facultatif"
                    />
                    {/* Sans TJM, les montants sont posés au jugé. Avec, le
                        chiffrage se dérive des jours et le client peut le refaire. */}
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                        Le chiffrage sera exprimé en jours multipliés par ce taux.
                    </p>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                    Réunion de cadrage
                </label>
                <textarea
                    className="input min-h-24"
                    value={form.transcript}
                    onChange={(event) => setForm({ ...form, transcript: event.target.value })}
                    placeholder="Coller ici le compte rendu ou le transcript de la réunion avec le client."
                />
                <input
                    type="file"
                    accept=".txt,.md,.vtt,.csv,.log,text/plain"
                    onChange={(event) => setTranscriptFile(event.target.files?.[0] || null)}
                    className="text-sm text-[var(--color-text-secondary)] mt-2"
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    Un fichier déposé remplace le texte collé. Formats texte : txt, md, vtt ou
                    csv. Un document Word se copie dans le champ ci-dessus.
                </p>
            </div>

            <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                    Documents du client
                </label>
                <input
                    type="file"
                    multiple
                    accept=".pdf,.docx,.pptx,.xlsx,.xlsm,.txt,.md,.csv,.tsv,.vtt,.log,.json,.xml"
                    onChange={(event) => setDocuments(Array.from(event.target.files || []))}
                    className="text-sm text-[var(--color-text-secondary)]"
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    Cahier des charges, note de contexte, schéma d'architecture, volumétries.
                    PDF, Word, PowerPoint, Excel, texte ou CSV, huit fichiers au plus. Leur
                    texte est extrait et transmis comme matière du dossier, jamais comme
                    consigne.
                </p>
                {documents.length > 0 && (
                    <ul className="text-xs text-[var(--color-text-secondary)] mt-2 space-y-0.5">
                        {documents.map((document) => (
                            <li key={document.name} className="flex items-center gap-1.5">
                                <FileText className="w-3 h-3 shrink-0" />
                                {document.name}
                                <span className="text-[var(--color-text-muted)]">
                                    {Math.max(1, Math.round(document.size / 1024))} Ko
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                    Proposition dont s'inspirer
                </label>
                <input
                    type="file"
                    accept=".pptx"
                    onChange={(event) => setDeck(event.target.files?.[0] || null)}
                    className="text-sm text-[var(--color-text-secondary)]"
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    Une proposition déjà livrée, au format PowerPoint. Son texte est extrait et
                    sert de référence de ton et de structure. Ni le client ni les montants qui
                    s'y trouvent ne sont repris.
                </p>
            </div>

            <div>
                <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                    Logo du client
                </label>
                <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={(event) => setLogo(event.target.files?.[0] || null)}
                    className="text-sm text-[var(--color-text-secondary)]"
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    Posé sur la page de titre, en haut à droite. PNG, JPEG, SVG ou WebP.
                </p>
            </div>

            <RunOptionsFields caps={caps} value={options} onChange={setOptions} />

            {error && (
                <p className="text-sm text-[var(--color-error)] flex gap-2">
                    <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    {error}
                </p>
            )}

            <div className="flex items-center gap-2">
                <button
                    className="btn-primary flex items-center gap-2"
                    disabled={disabled || tooShort || mutation.isPending}
                    onClick={() => { setError(null); mutation.mutate(); }}
                >
                    {mutation.isPending
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Sparkles className="w-4 h-4" />}
                    Générer la proposition
                </button>
                <button className="btn-secondary" onClick={onCancel} disabled={mutation.isPending}>
                    Annuler
                </button>
            </div>
        </div>
    );
}

export function ProposalCard({ proposal, expanded, onToggle, sharepointConfigured, caps }: {
    proposal: Proposal;
    expanded: boolean;
    onToggle: () => void;
    sharepointConfigured: boolean;
    caps?: ProposalCapabilities;
}) {
    const api = useApi();
    const queryClient = useQueryClient();
    // Une nouvelle version se choisit à nouveau, ce qui est tout l'intérêt :
    // reprendre en trame courte, ou avec un moteur moins coûteux, après avoir
    // vu ce que la première a donné.
    const [options, setOptions] = useState<RunOptionsState>(() => initialRunOptions(caps));
    const [showOptions, setShowOptions] = useState(false);
    // Les étapes de l'exécution en cours, remontées depuis le suivi : la
    // discussion s'en sert pour montrer le plan à mesure qu'il s'écrit, sans
    // interroger le journal une seconde fois.
    const [events, setEvents] = useState<ProposalRunEvent[]>([]);

    useEffect(() => {
        if (caps) setOptions((current) => (current.model ? current : initialRunOptions(caps)));
    }, [caps]);

    const { data: detail } = useQuery({
        queryKey: ['proposal', proposal.id],
        queryFn: () => api.get<ApiResponse<Proposal>>(`/api/proposals/${proposal.id}`),
        enabled: expanded,
        // Une génération en cours change d'état sans action de l'utilisateur.
        refetchInterval: proposal.status === 'generating' ? 2500 : false,
    });

    const full = detail?.data || proposal;
    const activeRun = useMemo(
        () => (full.runs || []).find((run) => run.status === 'queued' || run.status === 'running'),
        [full.runs],
    );

    const regenerate = useMutation({
        mutationFn: () => api.post<ApiResponse<{ runId: string }>>(
            `/api/proposals/${proposal.id}/generate`,
            {
                language: options.language || undefined,
                templateId: options.templateId || undefined,
                model: options.model || undefined,
                effort: options.effort || undefined,
                tools: JSON.stringify(options.tools),
                webUnrestricted: options.webUnrestricted ? 'true' : 'false',
                webAllowlist: options.webUnrestricted ? undefined : options.webAllowlist || undefined,
                libraryIds: options.libraryIds.length > 0 ? options.libraryIds : undefined,
                sections: options.sections.length > 0 ? options.sections : undefined,
            },
        ),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['proposal', proposal.id] });
            void queryClient.invalidateQueries({ queryKey: ['proposals', proposal.opportunityId] });
        },
    });

    return (
        <div className="rounded-lg border border-[var(--color-border)]">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-[var(--color-bg-hover)]"
            >
                <div className="min-w-0">
                    <p className="font-medium text-[var(--color-text)] truncate">
                        {full.title || 'Sans titre'}
                    </p>
                    <p className="text-xs text-[var(--color-text-secondary)] truncate">
                        {full.prompt}
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    {full.currentVersion > 0 && (
                        <span className="badge badge-default">v{full.currentVersion}</span>
                    )}
                    <StatusBadge status={full.status} />
                </div>
            </button>

            {expanded && (
                <div className="border-t border-[var(--color-border)] p-4 space-y-4">
                    {activeRun && (
                        <RunProgress
                            proposalId={proposal.id}
                            run={activeRun}
                            onEvents={setEvents}
                        />
                    )}

                    {/* La discussion vient avant les versions : c'est par elle
                        qu'on corrige, et regénérer depuis zéro n'est plus que
                        le dernier recours. */}
                    <ProposalChat
                        proposalId={proposal.id}
                        activeRun={activeRun}
                        events={activeRun ? events : []}
                        currentVersion={full.currentVersion}
                    />

                    {!activeRun && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <button
                                    className="btn-secondary flex items-center gap-2 text-sm"
                                    onClick={() => regenerate.mutate()}
                                    disabled={regenerate.isPending}
                                >
                                    {regenerate.isPending
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : <RefreshCw className="w-4 h-4" />}
                                    Générer une nouvelle version
                                </button>
                                <button
                                    className="text-sm text-[var(--color-text-secondary)] underline"
                                    onClick={() => setShowOptions((open) => !open)}
                                >
                                    {showOptions ? 'Masquer les options' : 'Options de génération'}
                                </button>
                            </div>
                            {showOptions && (
                                <RunOptionsFields caps={caps} value={options} onChange={setOptions} />
                            )}
                        </div>
                    )}

                    {regenerate.isError && (
                        <p className="text-sm text-[var(--color-error)]">
                            {(regenerate.error as ApiError).message}
                        </p>
                    )}

                    <FailedRun runs={full.runs || []} />

                    <Documents documents={full.documents || []} />

                    <Versions
                        proposal={full}
                        sharepointConfigured={sharepointConfigured}
                    />
                </div>
            )}
        </div>
    );
}

/**
 * Documents du client attachés à la proposition, et ce qui en a été lu.
 *
 * Le nombre de caractères n'est pas un détail technique : un PDF de quarante
 * pages fait de captures d'écran rend zéro caractère, et le consultant doit le
 * savoir avant de s'étonner que la proposition ignore son cahier des charges.
 */
export function Documents({ documents }: { documents: ProposalDocument[] }) {
    if (documents.length === 0) return null;

    return (
        <div className="space-y-1">
            <p className="text-xs font-medium text-[var(--color-text-secondary)]">
                Documents du client
            </p>
            {documents.map((document) => (
                <p
                    key={document.id}
                    className="text-xs text-[var(--color-text-secondary)] flex items-start gap-1.5"
                >
                    <FileText className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>
                        {document.filename}
                        {document.extractError ? (
                            <span className="text-[var(--color-warning)]">
                                {' '}— {document.extractError}
                            </span>
                        ) : (
                            <span className="text-[var(--color-text-muted)]">
                                {' '}— {document.chars.toLocaleString('fr-FR')} caractères lus
                            </span>
                        )}
                    </span>
                </p>
            ))}
        </div>
    );
}

export function StatusBadge({ status }: { status: string }) {
    const style: Record<string, string> = {
        ready: 'badge-success',
        failed: 'badge-error',
        generating: 'badge-warning',
    };
    return (
        <span className={`badge ${style[status] || 'badge-default'}`}>
            {STATUS_LABELS[status] || status}
        </span>
    );
}

/** Journal de l'exécution en cours, avec possibilité d'annuler. */
export function RunProgress({ proposalId, run, onEvents }: {
    proposalId: string;
    run: ProposalRun;
    /** Remonte le journal à la fenêtre, qui le partage avec la discussion. */
    onEvents?: (events: ProposalRunEvent[]) => void;
}) {
    const api = useApi();
    const queryClient = useQueryClient();

    const { data } = useQuery({
        queryKey: ['proposal-run', run.id],
        queryFn: () => api.get<ApiResponse<ProposalRun>>(
            `/api/proposals/${proposalId}/runs/${run.id}`,
        ),
        refetchInterval: 2000,
    });

    const cancel = useMutation({
        mutationFn: () => api.post(`/api/proposals/${proposalId}/runs/${run.id}/cancel`),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['proposal-run', run.id] });
            void queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] });
        },
    });

    const events = useMemo(() => data?.data.events || [], [data]);
    const last = events[events.length - 1];
    // L'annulation demandée est lue sur l'exécution et non sur la mutation :
    // celle-ci répond tout de suite, puisque la route ne fait que poser un
    // drapeau. Le bouton redevenait donc actif aussitôt, sans que rien à l'écran
    // n'indique que la demande était partie.
    const annulationDemandee = cancel.isSuccess || data?.data.cancelRequested === true;

    useEffect(() => {
        onEvents?.(events);
    }, [events, onEvents]);

    return (
        <div className="rounded-lg bg-[var(--color-bg-hover)] p-3 space-y-2">
            <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-[var(--color-text)] flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[var(--color-primary)]" />
                    {last ? libelleEtape(last.type) : 'Mise en file'}
                </p>
                <button
                    className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-error)]
                        disabled:hover:text-[var(--color-text-muted)] disabled:text-[var(--color-text-muted)]"
                    onClick={() => cancel.mutate()}
                    disabled={cancel.isPending || annulationDemandee}
                >
                    {annulationDemandee ? 'Annulation demandée' : 'Annuler'}
                </button>
            </div>
            {/* Dit explicitement ce que l'écran ne montrait pas : la génération
                se déroule côté serveur, elle ne dépend pas de cette fenêtre. */}
            <p className="text-xs text-[var(--color-text-muted)]">
                {annulationDemandee
                    ? "Annulation en cours : l'appel au modèle est interrompu, "
                      + "l'exécution s'arrête dans quelques secondes."
                    : 'Vous pouvez fermer cette fenêtre : la génération se poursuit, et son '
                      + "avancement s'affiche dans la liste des avant-ventes."}
            </p>
            <EtapesDuRun evenements={events} />
        </div>
    );
}

/**
 * Étapes d'une exécution, telles qu'elles se lisent.
 *
 * Sorti du suivi en cours pour servir aussi au journal d'une exécution passée.
 * Les événements sont conservés en base et rien ne les purge, mais ils n'étaient
 * affichés que pendant la génération : tout ce que le modèle avait cherché,
 * consulté et calculé devenait invisible dès le document produit, alors que
 * c'est après coup qu'on veut comprendre d'où sort un chiffre.
 */
export function EtapesDuRun({ evenements }: { evenements: ProposalRunEvent[] }) {
    return (
                <ol className="text-xs text-[var(--color-text-secondary)] space-y-0.5">
                    {evenements.map((event) => (
                        event.type === 'activite' ? (
                            // Un pas du modèle n'est pas une étape de l'orchestration :
                            // il n'en porte donc pas le libellé. Le répéter à chaque
                            // ligne remplissait le journal de « Travail du modèle ».
                            <PasDuModele key={event.seq} payload={event.payload} />
                        ) : (
                        <li key={event.seq}>
                            {libelleEtape(event.type)}
                            {event.type === 'plan_rejected' && event.payload?.problems?.length
                                ? ` : ${event.payload.problems[0]}`
                                : ''}
                            {event.type === 'plan_ready' && event.payload?.slides
                                ? ` : ${event.payload.slides} slides`
                                : ''}
                            {event.type === 'briefing_pret' && event.payload?.caracteres
                                ? ` : ${event.payload.caracteres} caractères de matière`
                                : ''}
                            {event.type === 'section_ecrite'
                                ? ` : ${event.payload?.section} (${event.payload?.rang}/`
                                  + `${event.payload?.total}), ${event.payload?.slides} slides`
                                : ''}
                            {event.type === 'plan_incomplet' && event.payload?.manques?.length
                                ? ` : ${event.payload.manques[0]}`
                                : ''}
                            {/* Ce que la relecture a trouvé et corrigé. Sans cela,
                                l'étape s'afficherait sans rien apprendre. */}
                            {event.type === 'relecture' && event.payload?.resume
                                ? ` : ${event.payload.resume}`
                                : ''}
                            {event.type === 'correction_appliquee'
                                ? ` : ${event.payload?.retenue ? 'retenue' : 'écartée'}, `
                                  + `${event.payload?.avant} défauts avant, ${event.payload?.apres} après`
                                : ''}
                        </li>
                        )
                    ))}
                </ol>
    );
}

/**
 * Un pas du modèle, tel qu'il se lit.
 *
 * La phrase est composée côté serveur, là où la structure de l'appel est encore
 * disponible : ici il n'y a plus qu'à la poser. Un point coloré marque la nature
 * du pas, ce qui suffit à distinguer une recherche d'un résultat sans écrire le
 * nom d'un outil que personne ne connaît.
 */
function PasDuModele({ payload }: { payload: Record<string, any> | undefined }) {
    const resume = String(payload?.resume || '').trim();
    if (!resume) return null;
    const genre = payload?.genre;

    const teinte = genre === 'resultat_outil'
        ? 'bg-[var(--color-success)]'
        : genre === 'reflexion' ? 'bg-[var(--color-text-muted)]' : 'bg-[var(--color-primary)]';

    return (
        <li className="flex list-none items-baseline gap-2 pl-3">
            <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${teinte}`} />
            <span
                className={[
                    'min-w-0 text-[var(--color-text-muted)]',
                    genre === 'reflexion' ? 'italic' : '',
                ].join(' ')}
            >
                {resume}
            </span>
        </li>
    );
}

/** Dernier échec, s'il n'a pas été suivi d'une réussite. */
export function FailedRun({ runs }: { runs: ProposalRun[] }) {
    const last = runs[0];
    if (!last || last.status !== 'failed' || !last.errorMessage) return null;
    return (
        <div className="rounded-lg border border-[var(--color-error)] bg-[var(--color-error-light)] p-3">
            <p className="text-sm text-[var(--color-text)] flex gap-2">
                <XCircle className="w-4 h-4 shrink-0 mt-0.5 text-[var(--color-error)]" />
                {last.errorMessage}
            </p>
        </div>
    );
}

export function Versions({ proposal, sharepointConfigured, onReprendre, occupe }: {
    proposal: Proposal;
    sharepointConfigured: boolean;
    /** Relance une génération à partir de cette version. */
    onReprendre?: (versionId: string) => void;
    /** Vrai pendant qu'une génération part, pour ne pas en lancer deux. */
    occupe?: boolean;
}) {
    const api = useApi();
    const queryClient = useQueryClient();
    const versions = proposal.versions || [];

    const deposit = useMutation({
        mutationFn: (versionId: string) => api.post(
            `/api/proposals/${proposal.id}/versions/${versionId}/deposit`,
        ),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['proposal', proposal.id] }),
    });

    if (versions.length === 0) {
        return (
            <p className="text-sm text-[var(--color-text-secondary)]">
                Aucune version produite pour le moment.
            </p>
        );
    }

    return (
        <div className="space-y-2">
            {versions.map((version) => (
                <div
                    key={version.id}
                    className="flex items-start justify-between gap-4 rounded-lg border border-[var(--color-border)] p-3"
                >
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-[var(--color-text)] flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[var(--color-text-secondary)]" />
                            Version {version.version}
                            <span className="text-xs font-normal text-[var(--color-text-muted)]">
                                {version.slideCount} slides
                            </span>
                        </p>
                        {version.consultantNotes && (
                            <p className="text-xs text-[var(--color-text-secondary)] mt-1 whitespace-pre-line">
                                {version.consultantNotes}
                            </p>
                        )}
                        {version.sharepointWebUrl ? (
                            <a
                                href={version.sharepointWebUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-[var(--color-primary)] hover:underline mt-1 inline-block"
                            >
                                Ouvrir dans SharePoint
                            </a>
                        ) : (
                            <p className="text-xs text-[var(--color-text-muted)] mt-1">
                                Pas encore déposée dans SharePoint.
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        {!version.sharepointWebUrl && sharepointConfigured && (
                            <button
                                className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)]"
                                title="Reprendre le dépôt SharePoint"
                                onClick={() => deposit.mutate(version.id)}
                                disabled={deposit.isPending}
                            >
                                <CloudUpload className="w-4 h-4 text-[var(--color-text-secondary)]" />
                            </button>
                        )}
                        {onReprendre && (
                            <button
                                className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)]"
                                title={`Repartir de la version ${version.version}`}
                                onClick={() => onReprendre(version.id)}
                                disabled={occupe}
                            >
                                <GitBranch className="w-4 h-4 text-[var(--color-text-secondary)]" />
                            </button>
                        )}
                        <button
                            className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)]"
                            title="Télécharger"
                            onClick={() => api.download(
                                `/api/proposals/${proposal.id}/versions/${version.id}/download`,
                                `${(proposal.title || 'proposition').replace(/[^\w-]+/g, '_')}_v${version.version}.pptx`,
                            )}
                        >
                            <Download className="w-4 h-4 text-[var(--color-text-secondary)]" />
                        </button>
                    </div>
                </div>
            ))}
            {deposit.isError && (
                <p className="text-sm text-[var(--color-error)]">
                    {(deposit.error as ApiError).message}
                </p>
            )}
            <AvantEnvoi />
            <Cost runs={proposal.runs || []} />
        </div>
    );
}

/**
 * Rappel de relecture, posé là où l'on récupère le document.
 *
 * Ce n'est pas une clause de style. Une proposition a déjà été envoyée à un
 * client en s'arrêtant au milieu de sa deuxième section, et personne ne l'avait
 * relue. Les garde-fous ajoutés depuis refusent un plan amputé, mais aucun
 * contrôle automatique ne juge si un chiffrage est juste ni si une démarche est
 * la bonne : cela reste le travail du consultant, et le dire à l'endroit et au
 * moment où il télécharge vaut mieux que de le supposer connu.
 */
function AvantEnvoi() {
    return (
        <div
            className="flex items-start gap-2 rounded-lg border border-[var(--color-warning)]
                bg-[var(--color-warning-light)] p-3"
        >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-warning)]" />
            <p className="text-xs text-[var(--color-text)]">
                <span className="font-medium">À relire avant envoi au client.</span>{' '}
                Les chiffrages, les charges et les engagements de délai sont proposés par
                le modèle : ils n'ont été vérifiés par personne. Contrôlez-les, ainsi que
                les noms propres et les montants, avant de transmettre le document.
            </p>
        </div>
    );
}

/**
 * Coût cumulé des générations de cette proposition.
 *
 * Il est affiché parce qu'une proposition coûte réellement quelque chose et que
 * le consultant est le mieux placé pour arbitrer une régénération. Le masquer
 * reviendrait à rendre cet arbitrage impossible.
 */
export function Cost({ runs }: { runs: ProposalRun[] }) {
    const total = runs.reduce((sum, run) => sum + (Number(run.costUsd) || 0), 0);
    if (total <= 0) return null;
    const label = total < 0.01 ? '< 0,01' : total.toFixed(2).replace('.', ',');
    return (
        <p className="text-xs text-[var(--color-text-muted)]">
            Coût cumulé des générations : {label} $ sur {runs.length} exécution
            {runs.length > 1 ? 's' : ''}.
        </p>
    );
}
